import { LevelType, SpaceType } from 'data/types';
import { spaceAbsolutePolygon } from 'utils/carve-auto-layout-space';
import { OpenToUpdateType } from 'utils/open-to-types';
import { getHiddenIntervalsPerEdge } from 'utils/polygon-shared-edges';

export function isOpenTo(space: SpaceType, otherSpaceId: string): boolean {
  return space.openTo?.includes(otherSpaceId) ?? false;
}

function hasOpenToLink(spaceA: SpaceType, spaceB: SpaceType): boolean {
  return isOpenTo(spaceA, spaceB.uniqueId) || isOpenTo(spaceB, spaceA.uniqueId);
}

function spacesShareEdge(spaceA: SpaceType, spaceB: SpaceType): boolean {
  const polygonA = spaceAbsolutePolygon(spaceA);
  const polygonB = spaceAbsolutePolygon(spaceB);
  if (!polygonA || !polygonB) {
    return false;
  }
  return getHiddenIntervalsPerEdge(polygonA, [polygonB]).some(
    (intervals) => intervals.length > 0
  );
}

function findSpace(structure: LevelType[], uniqueId: string): SpaceType | null {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === uniqueId) {
          return space;
        }
      }
    }
  }
  return null;
}

function withoutId(ids: string[] | undefined, idToRemove: string): string[] {
  return (ids ?? []).filter((id) => id !== idToRemove);
}

function withId(ids: string[] | undefined, idToAdd: string): string[] {
  if (ids?.includes(idToAdd)) {
    return ids;
  }
  return [...(ids ?? []), idToAdd];
}

export function computeOpenToToggle(
  structure: LevelType[],
  spaceAId: string,
  spaceBId: string
): { aOpenTo: string[]; bOpenTo: string[] } {
  const spaceA = findSpace(structure, spaceAId);
  const spaceB = findSpace(structure, spaceBId);

  const aAlreadyOpen = spaceA?.openTo?.includes(spaceBId) ?? false;
  const bAlreadyOpen = spaceB?.openTo?.includes(spaceAId) ?? false;
  const isOpen = aAlreadyOpen && bAlreadyOpen;

  if (isOpen) {
    return {
      aOpenTo: withoutId(spaceA?.openTo, spaceBId),
      bOpenTo: withoutId(spaceB?.openTo, spaceAId)
    };
  }

  return {
    aOpenTo: withId(spaceA?.openTo, spaceBId),
    bOpenTo: withId(spaceB?.openTo, spaceAId)
  };
}

export function computeOpenToAfterReshape(
  reshapedSpace: SpaceType,
  levelSpaces: SpaceType[]
): OpenToUpdateType[] {
  const linkedNeighbors = levelSpaces.filter(
    (neighbor) =>
      neighbor.uniqueId !== reshapedSpace.uniqueId &&
      hasOpenToLink(reshapedSpace, neighbor)
  );
  const retainedIds = linkedNeighbors
    .filter((neighbor) => spacesShareEdge(reshapedSpace, neighbor))
    .map((neighbor) => neighbor.uniqueId);

  const reshapedOpenTo = retainedIds.reduce(
    (ids, retainedId) => withId(ids, retainedId),
    (reshapedSpace.openTo ?? []).filter((id) => retainedIds.includes(id))
  );

  const neighborUpdates = linkedNeighbors.map((neighbor) => ({
    uniqueId: neighbor.uniqueId,
    openTo: retainedIds.includes(neighbor.uniqueId)
      ? withId(neighbor.openTo, reshapedSpace.uniqueId)
      : withoutId(neighbor.openTo, reshapedSpace.uniqueId)
  }));

  return [
    { uniqueId: reshapedSpace.uniqueId, openTo: reshapedOpenTo },
    ...neighborUpdates
  ];
}

function spacesAreTouching(a: SpaceType, b: SpaceType): boolean {
  if (
    !a.position ||
    !b.position ||
    a.width === undefined ||
    a.length === undefined ||
    b.width === undefined ||
    b.length === undefined
  ) {
    return false;
  }
  const aLeft = a.position.x;
  const aRight = a.position.x + a.width;
  const aTop = a.position.y;
  const aBottom = a.position.y + a.length;
  const bLeft = b.position.x;
  const bRight = b.position.x + b.width;
  const bTop = b.position.y;
  const bBottom = b.position.y + b.length;

  const horizontalOverlap = aLeft < bRight && aRight > bLeft;
  const verticalOverlap = aTop < bBottom && aBottom > bTop;

  const sharesHorizontalEdge =
    horizontalOverlap && (aTop === bBottom || aBottom === bTop);
  const sharesVerticalEdge =
    verticalOverlap && (aLeft === bRight || aRight === bLeft);

  return sharesHorizontalEdge || sharesVerticalEdge;
}

export function deriveOpenToFromOpenFloorPlan(
  structure: LevelType[]
): LevelType[] {
  const needsMigration = structure.some((level) =>
    level.sections.some((section) =>
      section.spaces.some((space) => space.openTo === undefined)
    )
  );

  if (!needsMigration) {
    return structure;
  }

  return structure.map((level) => {
    const allLevelSpaces = level.sections.flatMap((section) => section.spaces);

    return {
      ...level,
      sections: level.sections.map((section) => ({
        ...section,
        spaces: section.spaces.map((space) => {
          if (space.openTo !== undefined) {
            return space;
          }

          if (space.openFloorPlan === false) {
            return { ...space, openTo: [] };
          }

          const openTo: string[] = [];
          for (const neighbor of allLevelSpaces) {
            if (
              neighbor.uniqueId !== space.uniqueId &&
              neighbor.openFloorPlan !== false &&
              spacesAreTouching(space, neighbor)
            ) {
              openTo.push(neighbor.uniqueId);
            }
          }

          return { ...space, openTo };
        })
      }))
    };
  });
}
